"use client";

import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronLeft, MoreHorizontal, Play, Pause, Bookmark, Star, Clock, User, BookOpen, Layers, Search, Home, Activity } from "lucide-react";
import Image from "next/image";
import { useRef } from "react";

export default function FutureOfMusic() {
    const containerRef = useRef<HTMLElement>(null);
    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ["start end", "end start"]
    });

    const phoneY = useTransform(scrollYProgress, [0, 1], [200, -200]);

    return (
        <section ref={containerRef} className="relative w-full h-[1000px] md:h-[1200px] overflow-hidden bg-black text-white font-sans">

            {/* 1. Dynamic Background Gradient */}
            <div className="absolute inset-0 bg-gradient-to-b from-[#1e40af] via-[#3b82f6] to-[#050505] opacity-50 z-0" />
            <div className="absolute inset-0 bg-gradient-to-tr from-black/80 via-transparent to-transparent z-0" />

            {/* 2. Large Background Typography "Kitebe" */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full text-center z-0 select-none pointer-events-none mix-blend-overlay opacity-30">
                <h1 className="text-[25vw] md:text-[35vw] font-bold leading-none tracking-tighter text-white">Kitebe</h1>
            </div>


            {/* 3. Main Content Layer */}
            <div className="relative z-10 container mx-auto px-6 md:px-20 lg:px-40 h-full flex flex-col pt-24 mt-12 md:mt-0 md:pt-48 pb-12">

                {/* Header Text Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-10 items-start">
                    <h2 className="text-5xl sm:text-6xl md:text-8xl font-bold leading-[0.9] tracking-tighter text-white drop-shadow-xl">
                        App<br />
                        Development  <br />
                    </h2>
                    <div className="flex justify-start md:justify-end pt-2 sm:pt-4">
                        <p className="text-lg sm:text-xl md:text-2xl font-light leading-snug max-w-md lg:max-w-lg text-left md:text-right text-white/90">
                            We partner with ambitious founders and established brands to build mobile products that define categories. From MVP to Scaling, we are your technical co-founder.
                        </p>
                    </div>
                </div>

                {/* Phone Container */}
                <div className="absolute top-[40%] md:top-1/2 left-1/2 -translate-x-1/2 -translate-y-[40%] md:-translate-y-[45%] w-full h-full max-w-[1400px] pointer-events-none z-10 flex justify-center items-center">

                    {/* Phone Frame */}
                    <motion.div
                        style={{ y: phoneY }}
                        className="relative w-[280px] sm:w-[340px] h-[580px] sm:h-[700px] bg-white rounded-[40px] sm:rounded-[50px] border-[8px] border-[#e2e8f0] shadow-2xl overflow-hidden z-20 top-20 rotate-[-2deg]"
                    >

                        {/* Dynamic Island */}
                        <div className="absolute top-2 sm:top-3 left-1/2 -translate-x-1/2 w-24 sm:w-28 h-6 sm:h-8 bg-black rounded-full z-40 flex items-center justify-between px-4">
                            <div className="w-2 h-2 rounded-full bg-[#1a1a1a]" />
                        </div>

                        {/* Status Bar */}
                        <div className="absolute top-2 w-full px-6 flex justify-between text-[10px] font-bold text-slate-800 z-30 pt-2">
                            <span>9:41</span>
                            <div className="flex gap-1">
                                <div className="w-3 h-2 bg-slate-800 rounded-[2px]" />
                                <div className="w-3 h-2 bg-slate-800 rounded-[2px]" />
                            </div>
                        </div>

                        {/* Screen Content - eLearning App UI */}
                        <div className="w-full h-full relative bg-slate-50 flex flex-col font-sans">

                            {/* App Header */}
                            <div className="flex justify-between items-center px-4 sm:px-6 pt-12 sm:pt-14 pb-3 sm:pb-4 bg-white/50 backdrop-blur-sm sticky top-0 z-20">
                                <div className="flex flex-col">
                                    <span className="text-[9px] sm:text-xs font-semibold text-slate-500 uppercase tracking-wide">Good Morning</span>
                                    <h3 className="text-sm sm:text-xl font-bold text-slate-900">Alex Morgan</h3>
                                </div>
                                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-blue-100 flex items-center justify-center border-2 border-white shadow-sm overflow-hidden">
                                    <User className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                                </div>
                            </div>

                            {/* Search Bar */}
                            <div className="px-4 sm:px-6 mb-3 sm:mb-6">
                                <div className="bg-white rounded-xl sm:rounded-2xl p-2 sm:p-3 flex items-center gap-2 sm:gap-3 shadow-sm border border-slate-100">
                                    <div className="text-slate-400">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" className="w-4 h-4 sm:w-5 sm:h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>
                                    </div>
                                    <span className="text-xs sm:text-sm text-slate-400 font-medium">Find your course...</span>
                                </div>
                            </div>

                            {/* Currently Learning Card */}
                            <div className="px-4 sm:px-6 mb-4 sm:mb-8 relative z-10">
                                <div className="flex justify-between items-end mb-1 sm:mb-3">
                                    <h4 className="font-bold text-slate-800 text-sm sm:text-lg">Continue Learning</h4>
                                </div>
                                <div className="bg-[#1e40af] rounded-2xl sm:rounded-3xl p-3 sm:p-5 text-white shadow-lg shadow-blue-900/20 relative overflow-hidden group hover:scale-[1.02] transition-transform">
                                    <div className="absolute -right-4 -top-8 w-32 h-32 bg-blue-500 rounded-full opacity-30 blur-2xl group-hover:opacity-50 transition-opacity" />

                                    <div className="flex justify-between items-start mb-3 sm:mb-4 relative z-10">
                                        <span className="bg-white/20 backdrop-blur-md px-2.5 sm:px-3 py-1 rounded-full text-[9px] sm:text-[10px] font-bold uppercase tracking-wider">UX Design</span>
                                        <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-white/20 flex items-center justify-center">
                                            <Play className="w-3 h-3 sm:w-4 sm:h-4 fill-white text-white ml-0.5" />
                                        </div>
                                    </div>

                                    <h3 className="text-sm sm:text-xl font-bold mb-0.5 sm:mb-1 relative z-10">Advanced Prototyping</h3>
                                    <p className="text-blue-100 text-[9px] sm:text-xs mb-3 sm:mb-4 relative z-10">Chapter 4 • 24 mins left</p>

                                    {/* Progress Bar */}
                                    <div className="w-full h-1.5 bg-blue-900/40 rounded-full overflow-hidden relative z-10">
                                        <div className="w-[65%] h-full bg-white rounded-full shadow-[0_0_10px_rgba(255,255,255,0.5)]" />
                                    </div>
                                </div>
                            </div>

                            {/* Popular Courses Section */}
                            <div className="flex-1 px-4 sm:px-6 pb-14 sm:pb-20 overflow-hidden">
                                <div className="flex justify-between items-center mb-2 sm:mb-4">
                                    <h4 className="font-bold text-slate-800 text-sm sm:text-lg">Popular Courses</h4>
                                    <span className="text-[10px] sm:text-xs font-semibold text-blue-600">See All</span>
                                </div>

                                <div className="flex flex-col gap-2.5 sm:gap-4">
                                    {/* Course Item 1 */}
                                    <div className="bg-white p-2 sm:p-3 rounded-xl sm:rounded-2xl flex gap-3 sm:gap-4 shadow-sm border border-slate-100 items-center hover:shadow-md transition-shadow cursor-pointer group">
                                        <div className="w-10 h-10 sm:w-16 sm:h-16 rounded-lg sm:rounded-xl bg-orange-100 flex items-center justify-center text-orange-600 shrink-0 group-hover:scale-105 transition-transform">
                                            <Layers className="w-5 h-5 sm:w-8 sm:h-8" />
                                        </div>
                                        <div className="flex-1">
                                            <h5 className="font-bold text-slate-800 text-xs sm:text-sm mb-0 sm:mb-1">3D Illustration</h5>
                                            <div className="flex items-center gap-2 sm:gap-3 text-[9px] sm:text-xs text-slate-500 font-medium">
                                                <div className="flex items-center gap-1"><Star className="w-2.5 h-2.5 sm:w-3 sm:h-3 fill-yellow-400 text-yellow-400" /> 4.9</div>
                                                <div className="flex items-center gap-1"><Clock className="w-2.5 h-2.5 sm:w-3 sm:h-3" /> 6h 30m</div>
                                            </div>
                                        </div>
                                        <div className="pr-1 sm:pr-2">
                                            <ChevronLeft className="rotate-180 text-slate-300 w-4 h-4 sm:w-5 sm:h-5" />
                                        </div>
                                    </div>

                                    {/* Course Item 2 */}
                                    <div className="bg-white p-2 sm:p-3 rounded-xl sm:rounded-2xl flex gap-3 sm:gap-4 shadow-sm border border-slate-100 items-center hover:shadow-md transition-shadow cursor-pointer group">
                                        <div className="w-10 h-10 sm:w-16 sm:h-16 rounded-lg sm:rounded-xl bg-violet-100 flex items-center justify-center text-violet-600 shrink-0 group-hover:scale-105 transition-transform">
                                            <BookOpen className="w-5 h-5 sm:w-8 sm:h-8" />
                                        </div>
                                        <div className="flex-1">
                                            <h5 className="font-bold text-slate-800 text-xs sm:text-sm mb-0 sm:mb-1">Web Dev</h5>
                                            <div className="flex items-center gap-2 sm:gap-3 text-[9px] sm:text-xs text-slate-500 font-medium">
                                                <div className="flex items-center gap-1"><Star className="w-2.5 h-2.5 sm:w-3 sm:h-3 fill-yellow-400 text-yellow-400" /> 4.8</div>
                                                <div className="flex items-center gap-1"><Clock className="w-2.5 h-2.5 sm:w-3 sm:h-3" /> 12h 45m</div>
                                            </div>
                                        </div>
                                        <div className="pr-1 sm:pr-2">
                                            <ChevronLeft className="rotate-180 text-slate-300 w-4 h-4 sm:w-5 sm:h-5" />
                                        </div>
                                    </div>

                                    {/* Course Item 3 */}
                                    <div className="bg-white p-2 sm:p-3 rounded-xl sm:rounded-2xl gap-3 sm:gap-4 shadow-sm border border-slate-100 items-center hover:shadow-md transition-shadow cursor-pointer group opacity-60 hidden sm:flex">
                                        <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-lg sm:rounded-xl bg-green-100 flex items-center justify-center text-green-600 shrink-0 group-hover:scale-105 transition-transform">
                                            <Activity className="w-6 h-6 sm:w-8 sm:h-8" />
                                        </div>
                                        <div className="flex-1">
                                            <h5 className="font-bold text-slate-800 text-xs sm:text-sm mb-0.5 sm:mb-1">Marketing 101</h5>
                                            <div className="flex items-center gap-2 sm:gap-3 text-[10px] sm:text-xs text-slate-500 font-medium">
                                                <div className="flex items-center gap-1"><Star className="w-2.5 h-2.5 sm:w-3 sm:h-3 fill-yellow-400 text-yellow-400" /> 4.7</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Bottom Tab Bar */}
                            <div className="h-14 sm:h-20 bg-white border-t border-slate-100 flex items-center justify-around px-2 sticky bottom-0 z-30 pb-2 sm:pb-4">
                                <div className="p-1 sm:p-2 text-blue-600 flex flex-col items-center gap-1">
                                    <Home className="w-4 h-4 sm:w-6 sm:h-6" />
                                    <div className="w-1 h-1 bg-blue-600 rounded-full" />
                                </div>
                                <div className="p-1 sm:p-2 text-slate-400 hover:text-slate-600 transition-colors">
                                    <Search className="w-4 h-4 sm:w-6 sm:h-6" />
                                </div>
                                <div className="p-1 sm:p-2 text-slate-400 hover:text-slate-600 transition-colors">
                                    <Bookmark className="w-4 h-4 sm:w-6 sm:h-6" />
                                </div>
                                <div className="p-1 sm:p-2 text-slate-400 hover:text-slate-600 transition-colors">
                                    <User className="w-4 h-4 sm:w-6 sm:h-6" />
                                </div>
                            </div>

                        </div>
                    </motion.div>
                </div>


                {/* Text Labels Layer */}
                <div className="absolute inset-0 pointer-events-none z-20 hidden md:block">
                    <div className="absolute top-[65%] right-[20%] text-left text-sm font-medium text-slate-900 md:text-white/80 space-y-1">
                        <p>Research</p>
                        <p>Wireframing</p>
                        <p>Designing</p>
                        <p>Prototyping</p>
                    </div>
                </div>

            </div>
        </section>
    );
}
